import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, ArrowsClockwise, CalendarCheck } from "@phosphor-icons/react";

const STATUS_CHIP = {
  pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800", cancelled: "bg-slate-100 text-slate-500",
};
const fmtD = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
export const leaveLabel = (r) => (r.half_day ? `${fmtD(r.from_date)} · half day` : r.from_date === r.to_date ? fmtD(r.from_date) : `${fmtD(r.from_date)} → ${fmtD(r.to_date)}`);

export default function LeaveRequestsPanel({ onChanged, maxHeight = "380px" }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("pending");
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/payroll/leave-requests", { params: tab === "pending" ? { status: "pending" } : { limit: 100 } });
      setData(d);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const decide = async (r, action) => {
    let note = "";
    if (action === "reject") {
      note = window.prompt(`Reject ${r.name}'s leave (${leaveLabel(r)})?\n\nNote to record (optional):`, "");
      if (note === null) return;
    }
    setBusyId(r.id);
    try {
      const { data: d } = await api.post(`/payroll/leave-requests/${r.id}/${action}`, { note });
      if (action === "approve") toast.success(`Approved · ${d.cells_written} day${d.cells_written === 1 ? "" : "s"} marked ${d.type} in the Attendance Register`);
      else toast.success("Request rejected");
      await load();
      onChanged?.(d);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusyId(""); }
  };

  const rows = data?.requests || [];
  const pending = data?.counts?.pending ?? 0;
  return (
    <div className="border border-slate-200 bg-white rounded-sm flex flex-col h-full" data-testid="leave-requests-panel">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <CalendarCheck size={15} className="text-[#EA580C]" />
          <div className="text-[12.5px] font-bold text-slate-900">Leave Requests</div>
          {pending > 0 && <span className="h-5 min-w-5 px-1.5 rounded-full bg-[#EA580C] text-white text-[10px] font-bold flex items-center justify-center animate-pulse" data-testid="leave-pending-badge">{pending}</span>}
        </div>
        <div className="flex items-center gap-1">
          {["pending", "all"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`h-6 px-2 rounded-sm text-[10.5px] font-semibold capitalize ${tab === t ? "bg-[#0F172A] text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`leave-tab-${t}`}>{t}</button>
          ))}
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={load} data-testid="leave-refresh"><ArrowsClockwise size={12} /></Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-slate-100" style={{ maxHeight }} data-testid="leave-requests-list">
        {!data && <div className="text-center text-slate-400 text-[12px] py-8">Loading…</div>}
        {data && rows.length === 0 && (
          <div className="text-center text-slate-400 text-[12px] py-8" data-testid="leave-empty">
            {tab === "pending" ? "No pending requests — employees apply from the /punch page on their phone." : "No leave requests yet."}
          </div>
        )}
        {rows.map((r) => (
          <div key={r.id} className="px-3 py-2 flex items-start gap-3" data-testid={`leave-row-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[12px] font-semibold text-slate-900">{r.name}</span>
                <span className="text-[9.5px] text-slate-500 font-mono-num">{r.emp_code}</span>
                <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider ${STATUS_CHIP[r.status]}`} data-testid={`leave-status-${r.id}`}>{r.status}</span>
              </div>
              <div className="text-[11.5px] text-slate-800 mt-0.5">
                <span className="font-semibold">{leaveLabel(r)}</span>
                <span className="text-slate-500"> · {Number(r.days) % 1 ? r.days : Number(r.days)} day{Number(r.days) === 1 ? "" : "s"} · {r.type === "HD" ? "half day" : "LOP"}</span>
              </div>
              <div className="text-[11px] text-slate-600 italic truncate" title={r.reason}>“{r.reason}”</div>
              <div className="text-[9.5px] text-slate-400 mt-0.5">
                requested {new Date(r.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                {r.decided_at && r.status !== "pending" ? ` · ${r.status} ${r.decided_by && r.decided_by !== "employee" ? `by ${r.decided_by}` : ""}${r.admin_note ? ` — “${r.admin_note}”` : ""}` : ""}
              </div>
            </div>
            {r.status === "pending" && (
              <div className="flex flex-col gap-1 shrink-0">
                <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busyId === r.id} onClick={() => decide(r, "approve")} data-testid={`leave-approve-${r.id}`}><Check size={12} className="mr-1" weight="bold" /> Approve</Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-700 border-rose-200 hover:bg-rose-50" disabled={busyId === r.id} onClick={() => decide(r, "reject")} data-testid={`leave-reject-${r.id}`}><X size={12} className="mr-1" /> Reject</Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="px-3 py-1.5 border-t border-slate-100 text-[10px] text-slate-500 bg-slate-50/70">
        Approving writes <b>L</b> (or <b>HD</b> for a half day) into the register for working days — weekly offs &amp; holidays are skipped.
      </div>
    </div>
  );
}

export function LeaveRequestsDialog({ onClose, onChanged }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-[640px] p-0 overflow-hidden" data-testid="leave-requests-dialog">
        <DialogHeader className="px-4 pt-4 pb-0"><DialogTitle className="text-sm">Leave Requests</DialogTitle></DialogHeader>
        <div className="p-3"><LeaveRequestsPanel onChanged={onChanged} maxHeight="60vh" /></div>
      </DialogContent>
    </Dialog>
  );
}
